import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { fetchSlotDefinitions } from "@/lib/api/calendar";
import {
  subscribeAppointmentChanges,
  subscribeAppointmentRefresh,
} from "@/lib/realtime/appointment-realtime-bridge";
import { resolveTenantDisplayName } from "@/lib/tenant-display";

const UNASSIGNED_GROUP_KEY = "__unassigned__";
const UNASSIGNED_GROUP_LABEL = "Unassigned";

function asTrimmedString(value) {
  return typeof value === "string" ? value.trim() : "";
}

function eventGroupKey(row, groupBy) {
  if (groupBy === "tenant") {
    const id = asTrimmedString(row.tenantId);
    if (!id) return { key: UNASSIGNED_GROUP_KEY, label: UNASSIGNED_GROUP_LABEL };
    const label = asTrimmedString(row._tenant?.name) || "Clinic";
    return { key: id, label };
  }
  if (groupBy === "doctor") {
    const id = asTrimmedString(row.doctorId);
    if (!id) return { key: UNASSIGNED_GROUP_KEY, label: UNASSIGNED_GROUP_LABEL };
    const label = asTrimmedString(row.doctor) || asTrimmedString(row.doctorShort) || "Doctor";
    return { key: id, label };
  }
  return { key: "all", label: "All slots" };
}

function rowMatchesSearch(row, query) {
  if (!query) return true;
  const haystack = [
    row.title,
    row.placeholder,
    row.slotType,
    row.doctor,
    row.doctorShort,
    row._tenant?.name,
    row.location,
  ]
    .map(asTrimmedString)
    .filter(Boolean)
    .join(" · ")
    .toLowerCase();
  return haystack.includes(query);
}

function sortRows(rows, sortBy) {
  const copy = [...rows];
  if (sortBy === "created") {
    copy.sort((a, b) => {
      const ac = asTrimmedString(a.createdAt) || a.startAt?.toISO?.() || "";
      const bc = asTrimmedString(b.createdAt) || b.startAt?.toISO?.() || "";
      return bc.localeCompare(ac);
    });
    return copy;
  }
  copy.sort((a, b) => {
    const aMs = a.startAt?.toMillis?.() ?? 0;
    const bMs = b.startAt?.toMillis?.() ?? 0;
    return aMs - bMs;
  });
  return copy;
}

function groupRows(rows, groupBy) {
  const map = new Map();
  for (const row of rows) {
    const { key, label } = eventGroupKey(row, groupBy);
    let bucket = map.get(key);
    if (!bucket) {
      bucket = { key, label, rows: [] };
      map.set(key, bucket);
    }
    bucket.rows.push(row);
  }
  const groups = Array.from(map.values());
  groups.sort((a, b) => {
    if (a.key === UNASSIGNED_GROUP_KEY) return 1;
    if (b.key === UNASSIGNED_GROUP_KEY) return -1;
    return a.label.localeCompare(b.label);
  });
  return groups;
}

function resolveDoctorName(doctor, doctorId, doctorIndex) {
  if (doctor && typeof doctor === "object") {
    const firstName = asTrimmedString(doctor.firstName);
    const lastName = asTrimmedString(doctor.lastName);
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;
    const displayName = asTrimmedString(doctor.displayName);
    if (displayName) return displayName;
  }
  if (doctorIndex && doctorId) {
    const fromIndex = doctorIndex.get(doctorId);
    if (fromIndex) return fromIndex;
  }
  return "";
}

function resolveTenantName(tenant, tenantId, tenantIndex) {
  if (tenant && typeof tenant === "object") {
    const name = resolveTenantDisplayName(tenant);
    if (name) return name;
  }
  if (tenantIndex && tenantId) {
    const fromIndex = tenantIndex.get(tenantId);
    if (fromIndex) return fromIndex;
  }
  return "";
}

function mapApiItemToDefinitionRow(item, { doctorIndex, tenantIndex } = {}) {
  if (!item) return null;
  const id = String(item?.id || item?._id || "").trim();
  const startStr = String(item?.start || item?.time?.start || "").trim();
  const endStr = String(item?.end || item?.time?.end || "").trim();
  if (!id || !startStr || !endStr) return null;

  const startAt = DateTime.fromISO(startStr, { setZone: true }).toLocal();
  const endAt = DateTime.fromISO(endStr, { setZone: true }).toLocal();
  if (!startAt.isValid || !endAt.isValid) return null;

  let definitionId = String(item?.mongoSlotId || "").trim();
  if (!definitionId && id.startsWith("s-")) {
    const parts = id.split("-");
    if (parts.length >= 2) definitionId = parts[1];
  }
  if (!definitionId) definitionId = id;

  const recurrence =
    item?.recurrence && typeof item.recurrence === "object" ? item.recurrence : null;

  const doctorId = asTrimmedString(item?.doctorId) || undefined;
  const tenantId = asTrimmedString(item?.tenantId) || undefined;
  const doctorName = resolveDoctorName(item?._doctor, doctorId, doctorIndex);
  const tenantName = resolveTenantName(item?._tenant, tenantId, tenantIndex);

  return {
    id,
    definitionId,
    mongoSlotId: definitionId,
    kind: "slot",
    title: asTrimmedString(item?.title) || asTrimmedString(item?.placeholder) || "Slot",
    placeholder: asTrimmedString(item?.placeholder),
    slotType: asTrimmedString(item?.slotType) || "static",
    subType: asTrimmedString(item?.subType) || undefined,
    bookingMode: asTrimmedString(item?.bookingMode) || undefined,
    startAt,
    endAt,
    timezone: asTrimmedString(item?.timezone) || asTrimmedString(item?.time?.timezone) || undefined,
    doctorId,
    doctor: doctorName || undefined,
    doctorShort: doctorName || undefined,
    tenantId,
    tenantName: tenantName || undefined,
    _tenant: item?._tenant
      ? { ...item._tenant, ...(tenantName ? { name: tenantName } : {}) }
      : tenantName
        ? { name: tenantName }
        : undefined,
    calendarId: asTrimmedString(item?.calendarId) || undefined,
    metadata: item?.metadata || undefined,
    colorHex: asTrimmedString(item?.metadata?.colorHex) || undefined,
    recurrence,
    createdAt: asTrimmedString(item?.createdAt) || undefined,
    // Raw definitions are 1:1 with slot documents (no occurrence expansion), so
    // each row counts as a single occurrence for the heatmap matrix totals.
    occurrenceInRange: 1,
    raw: item,
  };
}

/**
 * Top-level hook for the slot definitions panel.
 *
 * Inputs: a date range (`fromDate`/`toDate` as Luxon DateTimes) plus panel-local
 * controls (search, group, sort). Fetches from `/api/calendar/slot-definitions`,
 * which returns raw slot definition documents — one row per slot with its
 * recurrence rule and metadata intact — so no client-side dedupe or
 * per-definition enrichment fetch is needed.
 */
export function useSlotDefinitions({
  fromDate,
  toDate,
  search,
  groupBy = "doctor",
  sortBy = "time",
  refreshKey = 0,
  doctorOptions,
  tenantOptions,
  enabled = true,
}) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bridgeVersion, setBridgeVersion] = useState(0);

  // Refetch on any cross-component slot change. We subscribe to BOTH bridge
  // channels deliberately: `subscribeAppointmentRefresh` catches explicit
  // `publishCalendarRefresh()` mutations, while `subscribeAppointmentChanges`
  // delivers realtime events *un-gated* by the calendar page's tenant/doctor
  // filter — a slot-definitions view must stay current regardless of it.
  useEffect(() => {
    const bump = () => setBridgeVersion((value) => value + 1);
    const unsubscribeRefresh = subscribeAppointmentRefresh(bump);
    const unsubscribeChanges = subscribeAppointmentChanges(bump);
    return () => {
      unsubscribeRefresh();
      unsubscribeChanges();
    };
  }, []);

  const rangeKey = useMemo(() => {
    const f = fromDate?.toUTC?.()?.toISO?.() || "";
    const t = toDate?.toUTC?.()?.toISO?.() || "";
    return `${enabled ? 1 : 0}|${f}|${t}|${refreshKey}|${bridgeVersion}`;
  }, [enabled, fromDate, toDate, refreshKey, bridgeVersion]);

  useEffect(() => {
    if (!enabled) return;
    if (!fromDate || !toDate) return;
    if (!fromDate.isValid || !toDate.isValid) return;
    if (toDate <= fromDate) return;

    let isMounted = true;
    setIsLoading(true);
    setError(null);
    fetchSlotDefinitions({
      start: fromDate.toUTC().toISO(),
      end: toDate.toUTC().toISO(),
      limit: 2000,
    })
      .then((response) => {
        if (!isMounted) return;
        setItems(Array.isArray(response?.items) ? response.items : []);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err);
        setItems([]);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  const indexes = useMemo(() => {
    const doctorIndex = new Map();
    if (Array.isArray(doctorOptions)) {
      for (const option of doctorOptions) {
        const id = asTrimmedString(option?.id);
        const name = asTrimmedString(option?.name);
        if (id && name) doctorIndex.set(id, name);
      }
    }
    const tenantIndex = new Map();
    if (Array.isArray(tenantOptions)) {
      for (const option of tenantOptions) {
        const id = asTrimmedString(option?.id);
        const name = asTrimmedString(option?.name);
        if (id && name) tenantIndex.set(id, name);
      }
    }
    return { doctorIndex, tenantIndex };
  }, [doctorOptions, tenantOptions]);

  // `/api/calendar/slot-definitions` already returns one row per slot document
  // with recurrence + metadata inline, so map straight to rows — no dedupe and
  // no per-definition enrichment fetch.
  const definitions = useMemo(
    () => items.map((item) => mapApiItemToDefinitionRow(item, indexes)).filter(Boolean),
    [items, indexes],
  );

  const groups = useMemo(() => {
    const query = asTrimmedString(search).toLowerCase();
    const matched = definitions.filter((row) => rowMatchesSearch(row, query));
    const sorted = sortRows(matched, sortBy);
    return groupRows(sorted, groupBy);
  }, [definitions, search, sortBy, groupBy]);

  const totalDefinitions = definitions.length;
  const totalOccurrences = items.length;

  return {
    groups,
    totalDefinitions,
    totalOccurrences,
    isLoading,
    error,
  };
}
